
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Check, Video } from 'lucide-react';
import { toast } from "../components/ui/use-toast";
import { recordAdWatched } from '../utils/cookieStorage';

interface AdModalProps {
  onClose: () => void;
  onAdComplete: () => void;
}

const AdModal = ({ onClose, onAdComplete }: AdModalProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const adContainerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Clean up ad if component unmounts during ad playback
    return () => {
      if (isPlaying && !isComplete) {
        // Handle any cleanup needed if ad is interrupted
      }
    };
  }, [isPlaying, isComplete]);

  // Function to initialize ad after clicking "Watch Ad"
  const initializeAd = () => {
    if (adContainerRef.current) {
      // Clear previous content if any
      adContainerRef.current.innerHTML = `
        <!-- Watch an ad -->
        <ins class="adsbygoogle"
             style="display:block"
             data-ad-client="ca-pub-4339436869702682"
             data-ad-slot="1257742356"
             data-ad-format="auto"
             data-full-width-responsive="true"></ins>
      `;

      // Push the ad
      try {
        // @ts-ignore - adsbygoogle is added by the script in index.html
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        console.log('AdSense ad pushed');
      } catch (error) {
        console.error('Error pushing AdSense ad:', error);
      }
    }
  };
  
  const handleWatchAd = () => {
    setIsPlaying(true);
    
    // Initialize the ad
    initializeAd();
    
    // Simulate ad playback with progress
    const duration = 15; // seconds - increased for real ad viewing
    const interval = 50; // ms
    const steps = (duration * 1000) / interval;
    let currentStep = 0;
    
    const timer = setInterval(() => {
      currentStep++;
      setProgress((currentStep / steps) * 100);
      
      if (currentStep >= steps) {
        clearInterval(timer);
        setIsPlaying(false);
        setIsComplete(true);
        recordAdWatched();
        toast({
          title: "Thank you!",
          description: "You can now get another fortune.",
        });
        setTimeout(() => {
          onAdComplete();
          onClose();
        }, 1500);
      }
    }, interval);
  };
  
  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center z-50 bg-black/20 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-white max-w-md w-full mx-4 p-8 rounded-lg text-center shadow-lg"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{ type: "spring", damping: 15 }}
        onClick={(e) => e.stopPropagation()}
      >
        {isComplete ? (
          <div className="py-4 flex flex-col items-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-medium text-gray-900">Thank You!</h3>
            <p className="text-gray-600 mt-2">You can now get another fortune.</p>
          </div>
        ) : (
          <>
            <h3 className="text-xl font-medium text-gray-900 mb-4">
              Get Another Fortune
            </h3>
            
            <p className="text-gray-600 mb-6">
              You've already received your daily fortune. Watch a short ad to get another one.
            </p>
            
            {isPlaying ? (
              <div className="w-full">
                <div 
                  ref={adContainerRef}
                  className="min-h-[250px] bg-gray-100 rounded-lg flex items-center justify-center mb-4"
                >
                  {/* AdSense ad will be inserted here */}
                  <Video className="w-12 h-12 text-gray-400 animate-pulse" />
                </div>
                <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                  <div 
                    className="bg-green-500 h-full rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500 mt-2">Please watch the ad completely...</p>
              </div>
            ) : (
              <motion.button
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium flex items-center justify-center space-x-2 w-full"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleWatchAd}
              >
                <Video className="w-5 h-5" />
                <span>Watch Ad</span>
              </motion.button>
            )}
            
            {!isPlaying && (
              <button 
                className="mt-4 text-sm text-gray-500 hover:text-gray-700"
                onClick={onClose}
              >
                Maybe later
              </button>
            )}
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default AdModal;
